import React from 'react';

import { styled } from '../theme';
import { Size, Status, matchSize } from '../utility/common-props-helpers';
import InputStatus from './InputStatus';
import SvgIcon from './SvgIcon';

/**
 * Utils
 */

const getHeight = matchSize({
    medium: 40,
    large: 50,
});

/**
 * Types
 */

type Props = {
    className?: string;
    style?: React.CSSProperties;
    innerInputStyle?: React.CSSProperties;
    innerRef?: any;
    error?: boolean;
    disabled?: boolean;
    required?: boolean;
    size?: Size;
    status?: Status;
    beforeIconSrc?: string;
    beforeIconColor?: string;
    as?: string;
    type?: string;
    placeholder?: string;
    value?: null | string;
    onChange?: (ev: any) => void;
    onKeyDown?: (ev: any) => void;
};

/**
 * Styled
 */

const Container = styled('div')({
    position: 'relative',
    outline: 'none',
});

const StyledIcon = styled(SvgIcon)<Props>(({ theme, beforeIconColor = 'currentColor', size }) => ({
    position: 'absolute',
    top: (getHeight(size) - 15) / 2,
    left: 15,
    svg: {
        fill: beforeIconColor,
    },
}));

const StyledInput = styled('input')<Props>(({ theme, disabled, error, size, beforeIconSrc }) => ({
    fontSize: '1em',
    color: theme.color.jetBlack,
    background: theme.color.secondary1,
    borderRadius: theme.borderRadius.base,
    lineHeight: matchSize({ medium: '20px', large: '30px' })(size),
    width: '100%',
    border: error ? '1px solid ' + theme.color.danger1 : 'none',
    padding: '10px 20px',
    ...(beforeIconSrc && { paddingLeft: 45 }),
    ...(disabled && {
        opacity: 0.5,
        color: theme.color.disabled,
    }),
}));

/**
 * Component
 */

export default React.forwardRef<HTMLInputElement, Props>(
    (
        {
            disabled,
            status,
            beforeIconSrc,
            beforeIconColor,
            size = 'large',
            className,
            style,
            innerInputStyle,
            as = 'input',
            ...props
        },
        ref
    ) => {
        const InnerInput = React.useCallback(StyledInput.withComponent(as), [as]);
        return (
            <Container style={style}>
                {beforeIconSrc && (
                    <StyledIcon
                        size={size}
                        alt="icon"
                        src={beforeIconSrc}
                        beforeIconColor={beforeIconColor}
                    />
                )}
                {status && (
                    <InputStatus
                        size={size}
                        status={status}
                        type={props.type}
                        name={props.name}
                        value={props.value}
                        onChange={props.onChange}
                    />
                )}
                <InnerInput
                    innerRef={ref}
                    className={className}
                    disabled={disabled}
                    beforeIconSrc={beforeIconSrc}
                    size={size}
                    style={innerInputStyle}
                    {...props}
                />
            </Container>
        );
    }
);
